import { useEffect, useState } from 'react';
import api from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { BarChart3, Clock, User, Package } from 'lucide-react';
import { toast } from 'sonner';

export default function Reporting() {
  const [report, setReport] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetchReport = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      const res = await api.get(`/routeops/reporting/picker-performance?${params.toString()}`);
      setReport(res.data || []);
    } catch {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReport(); }, [dateFrom, dateTo]);

  const fmtMins = (mins) => {
    if (mins == null) return '—';
    if (mins < 1) return `${Math.round(mins * 60)}s`;
    if (mins < 60) return `${mins} min`;
    const h = Math.floor(mins / 60);
    const m = Math.round(mins % 60);
    return `${h}h ${m}m`;
  };

  return (
    <div data-testid="routeops-reporting-page">
      <div className="flex items-center gap-3 pb-3 mb-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
        <BarChart3 className="h-5 w-5" style={{ color: 'var(--amber)' }} />
        <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)', fontFamily: 'var(--fd)' }}>Reporting</h1>
        <span className="text-xs uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Picker Performance</span>
      </div>

      {/* Filters */}
      <div className="flex items-end gap-4 mb-6">
        <div>
          <Label className="text-xs" style={{ color: 'var(--text-muted)' }}>From</Label>
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="h-9 w-44 text-sm"
            data-testid="report-date-from"
          />
        </div>
        <div>
          <Label className="text-xs" style={{ color: 'var(--text-muted)' }}>To</Label>
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="h-9 w-44 text-sm"
            data-testid="report-date-to"
          />
        </div>
        <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
          {report.length} result{report.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Summary Cards */}
      {report.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg" style={{ background: 'var(--amber-glow)' }}>
                <User className="h-5 w-5" style={{ color: 'var(--amber)' }} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)', fontFamily: 'var(--fm)' }}>
                  {new Set(report.map(r => r.picker_id)).size}
                </p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Active Pickers</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg" style={{ background: 'var(--green-dim)' }}>
                <Package className="h-5 w-5" style={{ color: 'var(--green)' }} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)', fontFamily: 'var(--fm)' }}>
                  {report.reduce((s, r) => s + r.total_orders, 0)}
                </p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Total Orders Picked</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg" style={{ background: 'var(--blue-dim)' }}>
                <Clock className="h-5 w-5" style={{ color: 'var(--blue)' }} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)', fontFamily: 'var(--fm)' }}>
                  {(() => {
                    const avgs = report.filter(r => r.avg_pick_time_mins != null).map(r => r.avg_pick_time_mins);
                    return avgs.length ? fmtMins(Math.round(avgs.reduce((s, v) => s + v, 0) / avgs.length)) : '—';
                  })()}
                </p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Avg Pick Time</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Report Table */}
      {report.map((entry, idx) => (
        <Card key={idx} className="mb-4">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CardTitle className="text-base" style={{ color: 'var(--text-primary)' }}>{entry.picker_name}</CardTitle>
                <Badge variant="outline" className="text-[10px]">{entry.picker_role}</Badge>
              </div>
              <div className="flex items-center gap-4 text-xs" style={{ color: 'var(--text-secondary)' }}>
                <span style={{ fontFamily: 'var(--fm)' }}>{entry.date}</span>
                <span><strong style={{ color: 'var(--text-primary)' }}>{entry.total_orders}</strong> orders</span>
                <span>Avg: <strong style={{ color: 'var(--amber)' }}>{fmtMins(entry.avg_pick_time_mins)}</strong></span>
                <span>Total: <strong>{fmtMins(entry.total_pick_time_mins)}</strong></span>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="rounded-lg border overflow-hidden" style={{ borderColor: 'var(--border-color)' }}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Business</TableHead>
                    <TableHead>Postcode</TableHead>
                    <TableHead className="text-right">Pallets</TableHead>
                    <TableHead className="text-right">Weight</TableHead>
                    <TableHead>Pick Start</TableHead>
                    <TableHead>Pick Finish</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entry.orders.map((o, oi) => (
                    <TableRow key={oi}>
                      <TableCell className="font-medium">{o.business_name}</TableCell>
                      <TableCell className="font-mono text-xs">{o.postcode}</TableCell>
                      <TableCell className="text-right font-mono">{o.pallets}</TableCell>
                      <TableCell className="text-right font-mono">{o.weight_kg ? `${o.weight_kg.toLocaleString()} KG` : 'TBD'}</TableCell>
                      <TableCell className="font-mono text-xs">{o.pick_start_at ? o.pick_start_at.slice(11, 19) : '—'}</TableCell>
                      <TableCell className="font-mono text-xs">{o.pick_finish_at ? o.pick_finish_at.slice(11, 19) : '—'}</TableCell>
                      <TableCell>
                        {o.duration_mins != null ? (
                          <span className="font-mono text-xs font-bold" style={{ color: 'var(--green)' }}>{fmtMins(o.duration_mins)}</span>
                        ) : (
                          <span style={{ color: 'var(--text-muted)' }}>—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-[10px] font-mono uppercase">{o.status}</span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      ))}

      {!loading && report.length === 0 && (
        <div className="text-center py-12" style={{ color: 'var(--text-muted)' }}>
          <BarChart3 className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No picker data found for the selected date range.</p>
          <p className="text-xs mt-1">Create bookings with pickers assigned to see performance data.</p>
        </div>
      )}

      {loading && <div className="text-center py-8 text-sm" style={{ color: 'var(--text-muted)' }}>Loading report...</div>}
    </div>
  );
}
